import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const SERVICES = [
  {
    icon: 'ri-heart-3-line',
    title: 'Wedding Venue',
    description:
      'An intimate yet grand setting for your most important day. Elegant indoor spaces, lush outdoor ceremony grounds, private bridal suites, and a team devoted to every detail.',
    cta: 'Explore Weddings',
    to: '/services',
    image:
      'https://public.readdy.ai/ai/img_res/2c2adac1d65a01d981788febc6ebacbd.jpg',
  },
  {
    icon: 'ri-goblet-2-line',
    title: 'Private Events',
    description:
      'From milestone birthdays to quinceañeras and family celebrations — our venue sets the stage for life\'s most meaningful private moments in an unforgettable setting.',
    cta: 'Plan Your Event',
    to: '/services',
    image:
      'https://readdy.ai/api/search-image?query=elegant-private-party-celebration-in-luxurious-event-venue-with-decorated-tables-ambient-lighting-floral-centerpieces-champagne-glasses-guests-mingling-warm-gold-tones-upscale-ranch-venue-interior&width=700&height=560&seq=services-private-card-01&orientation=portrait',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Corporate Events',
    description:
      'Elevate your next corporate retreat or company celebration with a venue that inspires genuine connection and leaves your team with a lasting impression.',
    cta: 'Corporate Inquiries',
    to: '/services',
    image:
      'https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/2e358b63-5ee5-487d-ab0e-aab0fb1485f4/IMG_9809.jpeg',
  },
  {
    icon: 'ri-landscape-2-line',
    title: 'Ranch Experiences',
    description:
      'Nestled on 20 hilltop acres, our outdoor spaces offer a naturally stunning backdrop for ceremonies, open-air receptions, and unforgettable celebrations under the Texas sky.',
    cta: 'Discover the Ranch',
    to: '/services',
    image:
      'https://readdy.ai/api/search-image?query=stunning-outdoor-hilltop-ranch-event-venue-at-twilight-with-string-lights-hanging-between-wooden-posts-guests-tables-lanterns-on-tables-rolling-texas-hills-in-background-warm-amber-romantic-evening-atmosphere-luxury-outdoor-wedding-reception&width=700&height=560&seq=services-ranch-card-01&orientation=portrait',
  },
];

export default function ServicesOverview() {
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: gridRef, isVisible: gridVisible } = useInView({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useInView();

  return (
    <section className="bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`mb-16 lg:mb-20 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-4 mb-5">
                <span className="gold-divider" />
                <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">What We Offer</p>
              </div>
              <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl leading-tight max-w-xl">
                Exceptional Spaces for Every Occasion
              </h2>
            </div>
            <Link
              to="/location#contact"
              className="flex-shrink-0 inline-block bg-rdr-charcoal text-white font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-rdr-warm transition-colors duration-300 whitespace-nowrap cursor-pointer self-start lg:self-auto"
            >
              Check Availability
            </Link>
          </div>
        </div>

        {/* Services Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {SERVICES.map((service, i) => (
            <div
              key={i}
              className={`group relative overflow-hidden fade-up ${gridVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              {/* Image */}
              <div className="w-full h-80 img-hover-zoom overflow-hidden relative">
                <img
                  src={service.image}
                  alt={`${service.title} at Red Door Ranch Joshua TX`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark/85 via-rdr-dark/25 to-transparent" />
              </div>

              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className={`${service.icon} text-lg text-rdr-gold`} />
                </div>
                <h3 className="font-serif text-white text-2xl lg:text-3xl mb-3">{service.title}</h3>
                <p className="text-white/65 font-sans text-sm leading-relaxed mb-5 max-w-sm">
                  {service.description}
                </p>
                <Link
                  to={service.to}
                  className="inline-flex items-center gap-2 text-rdr-gold font-sans text-xs tracking-[0.2em] uppercase hover:text-white transition-colors duration-300 cursor-pointer group"
                >
                  {service.cta}
                  <div className="w-4 h-4 flex items-center justify-center transform group-hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Post-Services CTA strip */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`mt-14 lg:mt-16 bg-rdr-cream border border-rdr-cream-dark p-10 lg:p-12 flex flex-col lg:flex-row items-center lg:items-center justify-between gap-8 fade-up ${ctaVisible ? 'is-visible' : ''}`}
        >
          <div>
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans mb-2">Ready to Book?</p>
            <h3 className="font-serif text-rdr-charcoal text-2xl lg:text-3xl leading-snug max-w-lg">
              Your Dream Event Starts with a Single Conversation
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-shrink-0">
            <Link
              to="/location#contact"
              className="inline-block bg-rdr-gold text-white font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Schedule a Tour
            </Link>
            <a
              href="tel:+18175048501"
              className="inline-flex items-center gap-2 text-rdr-charcoal font-sans text-xs tracking-[0.18em] uppercase border-b border-rdr-charcoal/30 pb-1 hover:text-rdr-gold hover:border-rdr-gold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              (817) 504-8501
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
